/**
 * W-6 商品详情(WScreen-6 product-detail.md)。桌面两栏:左图集/详情/评价,右 sticky 价格卡 + CTA。
 * 五态:加载=整页骨架;错误按 error.code 整页占位;默认=全字段 + 逐字段兜底;无整页空态。
 * CTA:立即购买/聊一聊 经 useAuthRequired 把关衔接下游占位路由(浏览→下单衔接点)。
 * 社交:收藏/点赞/关注 乐观切换(消费现有端点),飞行中忽略再点,失败回滚 + Toast。
 * 举报(⋯ 更多 → 举报违规商品 / 卖家卡 → 举报卖家)经 useAuthRequired 把关,开 ReportSheet(Story 30-3)。
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  addFavorite,
  followSeller,
  likeProduct,
  removeFavorite,
  unfollowSeller,
  unlikeProduct,
} from '@/api/guide';
import { useAuthRequired } from '@/router/useAuthRequired';
import { useAuthStore } from '@/store/auth';
import { useCategories, useProductDetail, useProductReviews } from '@/hooks/browse';
import { findL1 } from '@/lib/categoryTree';
import { UNIT_LABEL, MEDIUM_LABEL } from '@/lib/productLabels';
import { formatRating, formatYuan } from '@/lib/format';
import { detailErrorMessage, socialErrorMessage } from '@/lib/errors';
import { StateBlock } from '@/components/browse/StateBlock';
import { Breadcrumb, type Crumb } from '@/components/browse/Breadcrumb';
import { Toast } from '@/components/browse/Toast';
import { ReportSheet } from '@/components/report/ReportSheet';
import type { ReportTargetTypeCode } from '@/api/report';

export function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const requireAuth = useAuthRequired();
  const currentUserId = useAuthStore((s) => s.userId);

  const detail = useProductDetail(id);
  const reviews = useProductReviews(id);
  const categoriesQ = useCategories();
  const data = detail.data;

  // 社交本地乐观态(detail 到达后同步初值)
  const [fav, setFav] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [following, setFollowing] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  // 举报:菜单开合 + 当前举报对象（null=关闭）。PRODUCT=商品 / SELLER=卖家（Story 30-3）。
  const [moreOpen, setMoreOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: ReportTargetTypeCode; id?: string } | null>(null);

  useEffect(() => {
    if (data) {
      setFav(data.favorited ?? false);
      setLiked(data.liked ?? false);
      setLikeCount(data.likeCount ?? 0);
      setFollowing(data.followingSeller ?? false);
      setSelectedImg(0);
    }
  }, [data]);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const showToast = (m: string) => {
    setToast(m);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2400);
  };

  const favMut = useMutation({
    mutationFn: (next: boolean) => (next ? addFavorite(id as string) : removeFavorite(id as string)),
    onMutate: (next) => setFav(next),
    onError: (err, next) => {
      setFav(!next);
      showToast(socialErrorMessage(err, next ? '收藏失败,请稍后重试' : '取消收藏失败,请稍后重试'));
    },
  });
  const likeMut = useMutation({
    mutationFn: (next: boolean) => (next ? likeProduct(id as string) : unlikeProduct(id as string)),
    onMutate: (next) => {
      setLiked(next);
      setLikeCount((c) => Math.max(0, c + (next ? 1 : -1)));
    },
    onError: (err, next) => {
      setLiked(!next);
      setLikeCount((c) => Math.max(0, c + (next ? -1 : 1)));
      showToast(socialErrorMessage(err, next ? '点赞失败,请稍后重试' : '取消点赞失败,请稍后重试'));
    },
  });
  const sellerId = data?.seller?.sellerId;
  const followMut = useMutation({
    mutationFn: (next: boolean) =>
      next ? followSeller(sellerId as string) : unfollowSeller(sellerId as string),
    onMutate: (next) => setFollowing(next),
    onError: (err, next) => {
      setFollowing(!next);
      showToast(socialErrorMessage(err, next ? '关注失败,请稍后重试' : '取消关注失败,请稍后重试'));
    },
  });

  // ---- 五态短路(hooks 已全部无条件执行,可安全早返回)----
  if (!id) {
    return (
      <div className="inner section">
        <StateBlock icon="🔗" title="链接无效" desc="请从列表重新进入" actionLabel="返回首页" onAction={() => navigate('/')} />
      </div>
    );
  }
  if (detail.isLoading) {
    return (
      <div className="inner section">
        <DetailSkeleton />
      </div>
    );
  }
  if (detail.isError || !data) {
    return (
      <div className="inner section">
        <StateBlock
          icon="⚠️"
          title={detailErrorMessage(detail.error)}
          desc="可返回浏览其他商品"
          actionLabel="返回首页"
          onAction={() => navigate('/')}
        />
      </div>
    );
  }

  // ---- 默认/已加载:逐字段兜底 ----
  const catName = findL1(categoriesQ.data ?? [], data.categoryL1Id)?.name;
  const images = data.coverImages ?? [];
  const mainImg = images[selectedImg] ?? images[0];
  const isOwnProduct = !!currentUserId && sellerId === currentUserId;
  const summary = data.reviewSummary;
  const reviewCount = summary?.count ?? 0;
  const seller = data.seller;
  const reviewItems = reviews.data?.items ?? [];

  const crumbs: Crumb[] = [{ label: '首页', to: '/' }];
  if (catName && data.categoryL1Id) crumbs.push({ label: catName, to: `/category/${data.categoryL1Id}` });
  crumbs.push({ label: data.title ?? '商品详情' });

  // W-9 order-preview 经 ?productId= query 读取(与本页解耦);故用 query 传参,不用 route state
  // (iter-008 集成修:原用 { state } 与 order-preview 的 useSearchParams 契约不匹配 → 下单页「缺少商品参数」)。
  const onBuy = () =>
    requireAuth(() =>
      navigate(`/order-preview?productId=${encodeURIComponent(id)}`, {
        state: { coverImage: images[0] ?? null },
      }),
    );
  const onChat = () =>
    requireAuth(() => {
      if (!sellerId) return; // sellerId 缺失不发(product-detail.md 交互元素)
      navigate('/conversations', { state: { peerUserId: sellerId, seedProductId: id } });
    });

  return (
    <div className="inner section">
      <Breadcrumb items={crumbs} />

      <div className="detail-cols">
        {/* 左栏:图集 + 详情 + 评价 */}
        <div>
          <div
            className="card"
            style={{
              aspectRatio: '4 / 3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-muted-foreground)',
            }}
          >
            {mainImg ? (
              <img src={mainImg} alt={data.title ?? '商品大图'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              '商品大图'
            )}
          </div>
          {images.length > 1 ? (
            <div className="row gap-4 mt-4 wrap">
              {images.map((img, i) => (
                <button
                  key={`${img}-${i}`}
                  type="button"
                  className="card"
                  onClick={() => setSelectedImg(i)}
                  style={{
                    width: 96,
                    height: 72,
                    padding: 0,
                    overflow: 'hidden',
                    border: i === selectedImg ? '2px solid var(--color-primary)' : undefined,
                    cursor: 'pointer',
                  }}
                  aria-label={`查看第 ${i + 1} 张图`}
                >
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          ) : null}

          <div className="card card-pad mt-8">
            <h3 className="h3 mb-4">商品介绍</h3>
            <p className="muted" style={{ lineHeight: 2, whiteSpace: 'pre-wrap' }}>
              {data.description ?? '暂无商品介绍'}
            </p>
          </div>

          <div className="card card-pad mt-8">
            <div className="row between mb-4">
              <h3 className="h3">用户评价</h3>
              <span className="muted sm">
                {reviewCount > 0 ? `综合 ${formatRating(summary?.averageScore)} · ${reviewCount} 条` : '暂无评价'}
              </span>
            </div>
            {reviewItems.length > 0 ? (
              reviewItems.map((r, i) => (
                <div key={r.reviewId ?? `rv-${i}`} className="list-item" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <div className="avatar">{(r.buyerId || '匿')[0]}</div>
                  <div>
                    <div className="row gap-2">
                      <b>买家</b>
                      <span className="price" style={{ fontSize: 'var(--text-sm-size)' }}>
                        {'★'.repeat(Math.max(0, Math.min(5, Math.round(r.rating ?? 0))))}
                      </span>
                    </div>
                    <div className="sm muted">{r.content ?? ''}</div>
                  </div>
                </div>
              ))
            ) : (
              <p className="muted sm">暂无评价</p>
            )}
          </div>
        </div>

        {/* 右栏:sticky 价格卡 + 操作 + CTA */}
        <aside className="sticky-cta">
          <div className="card card-pad">
            <div className="row between mb-3" style={{ alignItems: 'flex-start' }}>
              <h1 className="h2" style={{ flex: 1 }}>
                {data.title ?? '未命名商品'}
              </h1>
              {/* 操作区:收藏 / 点赞 */}
              <div className="row gap-2" style={{ flexShrink: 0 }}>
                <button
                  type="button"
                  className="icon-btn"
                  aria-pressed={fav}
                  title={fav ? '取消收藏' : '收藏'}
                  onClick={() => {
                    if (favMut.isPending) return;
                    requireAuth(() => favMut.mutate(!fav));
                  }}
                >
                  <HeartIcon filled={fav} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-pressed={liked}
                  title={liked ? '取消点赞' : '点赞'}
                  onClick={() => {
                    if (likeMut.isPending) return;
                    requireAuth(() => likeMut.mutate(!liked));
                  }}
                >
                  <span className="row gap-1" style={{ alignItems: 'center' }}>
                    <ThumbIcon filled={liked} /> {likeCount}
                  </span>
                </button>
                {/* ⋯ 更多 → 举报违规商品(PRODUCT)。留本页,经 useAuthRequired 把关。
                    own-product 不显示举报入口(与卖家卡的 !isOwnProduct 守卫对称;后端亦 020306 兜底)。 */}
                {!isOwnProduct ? (
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-haspopup="menu"
                    aria-expanded={moreOpen}
                    title="更多"
                    onClick={() => setMoreOpen((v) => !v)}
                  >
                    ⋯
                  </button>
                  {moreOpen ? (
                    <div
                      role="menu"
                      className="card"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        marginTop: 'var(--space-2)',
                        minWidth: 140,
                        padding: 'var(--space-2)',
                        zIndex: 10,
                      }}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="btn btn-secondary sm btn-block"
                        onClick={() => {
                          setMoreOpen(false);
                          requireAuth(() => setReportTarget({ type: 'PRODUCT', id }));
                        }}
                      >
                        举报违规商品
                      </button>
                    </div>
                  ) : null}
                </div>
                ) : null}
              </div>
            </div>

            <div className="row between mb-4">
              <span className="price" style={{ fontSize: 'var(--text-3xl-size)' }}>
                ¥{formatYuan(data.price)}
                {data.unit ? <span className="unit">/{UNIT_LABEL[data.unit] ?? data.unit}</span> : null}
              </span>
              <span className="sm muted">
                {(data.salesCount ?? 0) > 0 ? `已成交 ${data.salesCount} 单` : ''}
              </span>
            </div>
            {/* 实物商品配送方式(✎010):仅快递/自提显示;虚拟介质不渲染、不采集收货地址(收货经 IM/自提)。 */}
            {data.medium === 'EXPRESS' || data.medium === 'PICKUP' ? (
              <div className="sm muted mb-2">配送方式:{MEDIUM_LABEL[data.medium] ?? data.medium}</div>
            ) : null}
            <div className="sm muted mb-6">评分 {formatRating(data.rating)}</div>

            {/* 卖家小卡 */}
            <div className="row gap-4 mb-6">
              <div className="avatar avatar-lg">{(seller?.nickname || '匿')[0]}</div>
              <div style={{ flex: 1 }}>
                <div className="row gap-2 between">
                  <b>{seller?.nickname ?? '匿名'}</b>
                  {!isOwnProduct && sellerId ? (
                    <button
                      type="button"
                      className={following ? 'btn btn-secondary sm' : 'btn btn-primary sm'}
                      onClick={() => {
                        if (followMut.isPending) return;
                        requireAuth(() => followMut.mutate(!following));
                      }}
                    >
                      {following ? '已关注' : '+ 关注'}
                    </button>
                  ) : null}
                </div>
                <div className="sm muted mt-2">
                  综合评分 {formatRating(seller?.rating)} · 在售 {seller?.salesCount ?? 0} 个商品
                </div>
                {!isOwnProduct && sellerId ? (
                  <button
                    type="button"
                    className="link-btn xs muted mt-2"
                    onClick={() => requireAuth(() => setReportTarget({ type: 'SELLER', id: sellerId }))}
                  >
                    举报卖家
                  </button>
                ) : null}
              </div>
            </div>

            {isOwnProduct ? (
              <>
                <p className="sm muted mb-3">这是你发布的商品,不能购买自己的商品。</p>
                <button
                  type="button"
                  className="btn btn-primary btn-lg btn-block mb-3"
                  onClick={() => navigate(`/publish?mode=edit&productId=${encodeURIComponent(id)}`)}
                >
                  编辑商品
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-lg btn-block"
                  onClick={() => navigate('/seller-products')}
                >
                  管理我的发布
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-primary btn-lg btn-block mb-3" onClick={onBuy}>
                  立即购买
                </button>
                <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={onChat}>
                  先聊一聊
                </button>
                <p className="xs muted mt-4">
                  下单后可在订单内与对方沟通,完成后双方互评。平台对成交收取 5% 服务费。
                </p>
              </>
            )}
          </div>
        </aside>
      </div>

      {toast ? <Toast message={toast} /> : null}

      {/* key 按对象重挂:PRODUCT↔SELLER 切换 / 关闭时重挂 → 丢弃上一入口的在飞上传/提交态,
          防证据图串入另一份举报(code-review Edge-Case #3)。 */}
      <ReportSheet
        key={reportTarget ? `${reportTarget.type}:${reportTarget.id ?? ''}` : 'closed'}
        open={!!reportTarget}
        targetType={reportTarget?.type ?? 'PRODUCT'}
        targetId={reportTarget?.id}
        onClose={() => setReportTarget(null)}
      />
    </div>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill={filled ? 'var(--color-primary)' : 'none'}
      stroke={filled ? 'var(--color-primary)' : 'currentColor'}
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20.3 4.2 12.5a4.7 4.7 0 0 1 6.6-6.6l1.2 1.2 1.2-1.2a4.7 4.7 0 1 1 6.6 6.6z" />
    </svg>
  );
}

function ThumbIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? 'var(--color-primary)' : 'none'}
      stroke={filled ? 'var(--color-primary)' : 'currentColor'}
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 10v10H4V10zM7 10l4.5-7a1.8 1.8 0 0 1 3 1.9L13 9h5.5a2 2 0 0 1 2 2.4l-1.3 6.5A2 2 0 0 1 17.2 20H7z" />
    </svg>
  );
}

function DetailSkeleton() {
  return (
    <div className="detail-cols" aria-busy="true">
      <div>
        <div className="card skeleton" style={{ aspectRatio: '4 / 3' }} />
        <div className="card card-pad mt-8">
          <div className="skeleton" style={{ height: 18, width: '30%', marginBottom: 16 }} />
          <div className="skeleton" style={{ height: 12, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 12, width: '80%' }} />
        </div>
      </div>
      <aside>
        <div className="card card-pad">
          <div className="skeleton" style={{ height: 24, marginBottom: 16 }} />
          <div className="skeleton" style={{ height: 28, width: '40%', marginBottom: 24 }} />
          <div className="skeleton" style={{ height: 44, marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 44 }} />
        </div>
      </aside>
    </div>
  );
}
